import { RUDDERSTACK_READY_RETRY } from 'constants/common'
import { IdentityUserTypes } from 'dataSource/auth/types'

export function setRudderstackAnonymousId(
  anonymousId: string,
  retryCount = 0
): void {
  if (globalThis.rudderanalytics) {
    globalThis.rudderanalytics.setAnonymousId(anonymousId)
    logIdentity() // This will stay anonymous but will then add the userType to the previously logged in userType
    return
  }

  // Split's anonymous client can be ready well before RudderStack's async SDK
  // import finishes loading, so this must retry rather than drop the id — a
  // silent no-op here leaves RudderStack tracking under its own anonymous id,
  // permanently disconnected from the one Split bucketed the session under.
  if (retryCount >= RUDDERSTACK_READY_RETRY.MAX_RETRIES) {
    // eslint-disable-next-line no-console
    console.error(
      'setRudderstackAnonymousId failed after maximum retries, rudderanalytics not initialized'
    )
    return
  }

  setTimeout(() => {
    setRudderstackAnonymousId(anonymousId, retryCount + 1)
  }, RUDDERSTACK_READY_RETRY.DELAY_MS)
}

export function saveRoleToLocalStorage(userType: IdentityUserTypes): void {
  localStorage.setItem('userType', userType)
}

// If the user is logged in, we will log their identity to rudderstack and save their userType to localStorage
// If the user is not logged in, we will log their anonymousId to rudderstack and use their saved userType from the previous login

const internalRoles = [IdentityUserTypes.SUPPORT, IdentityUserTypes.CLINICIAN]

export const logIdentity = (userId = '', userType = null): void => {
  let role = userType

  if (userType) {
    saveRoleToLocalStorage(userType)
  } else {
    role = localStorage.getItem('userType')
  }

  const internal = internalRoles.includes(role)
  const userProperties = role ? { userType: role, internal } : { internal }

  // `heapInit.js` defines globalThis.heap as an array carrying only `load` — the
  // tracking methods appear once `heap.load()` runs, which never happens without
  // analytics consent. So check for the method, not just the namespace.
  // FYI: globalThis is the way you are supposed to now do things that we used to do with window.*
  const heap = globalThis.heap

  // Heap specifically requests that you don't use non-unique values for identify
  // so doing a little extra check here to make sure this wasn't called blank.
  if (typeof heap?.identify === 'function' && userId !== '') {
    heap.identify(userId)
    heap.addUserProperties?.(userProperties)
  }

  // Register with Rudderstack, and check it's loaded first too
  if (globalThis.rudderanalytics) {
    const rudderUserId =
      userId || globalThis.rudderanalytics.getUserId?.() || ''
    globalThis.rudderanalytics.identify(rudderUserId, userProperties)
  }
}
